"use client"

/**
 * DeviceRow.jsx - Fleet Premium
 *
 * 2ECOMP: PremiumUI Refresh — item compacto 56px (2 linhas)
 *
 * Layout: [Checkbox] [StatusDot] [Nome + Timestamp] [Velocidade] [⋮ Menu]
 * Altura fixa: DEVICE_ROW_HEIGHT (56px)
 * Click = selecionar device (sem expand/collapse)
 * Menu ⋮ preservado: Seguir, Eventos, Histórico, Comando, Editar
 */

import {
  memo, useCallback, useState,
} from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { makeStyles } from "tss-react/mui"
import {
  Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Checkbox,
} from "@mui/material"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import NavigationIcon from "@mui/icons-material/Navigation"
import EventNoteIcon from "@mui/icons-material/EventNote"
import HistoryIcon from "@mui/icons-material/History"
import TerminalIcon from "@mui/icons-material/Terminal"
import EditIcon from "@mui/icons-material/Edit"
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove"
import { devicesActions } from "../store"
import { useTranslation } from "../common/components/LocalizationProvider"
import { getEffectiveStatus } from "../common/util/deviceColors"
import { deviceHasCamera } from "../common/util/cameras"
import CommandModal from "./components/CommandModal"
import { formatTime } from "../common/util/formatter"
import {
  DEVICE_ROW_HEIGHT,
  SELECTION_TRANSITION,
} from "../common/theme/layout"

// ============================================
// CONSTANTES - Cores estáticas (evita recálculo)
// ============================================
const STATUS_COLORS = {
  moving: '#3b82f6',   // Azul
  online: '#22c55e',   // Verde
  stopped: '#22c55e',  // Verde
  offline: '#ef4444',  // Vermelho
  unknown: '#6b7280',  // Cinza
  idle: '#f59e0b',     // Âmbar
  alarm: '#ef4444',    // Vermelho
}

// ============================================
// HELPERS - Fora do componente (sem recriação)
// ============================================
const getStatusColor = (effectiveStatus, hasAlarm) => {
  if (hasAlarm) return STATUS_COLORS.alarm
  return STATUS_COLORS[effectiveStatus] || STATUS_COLORS.idle
}

// ============================================
// STYLES — 2ECOMP: PremiumUI Refresh (48px compacto)
// ============================================
const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark'

  return {
    // 2ECOMP: PremiumUI Refresh — container 48px fixo
    row: {
      height: DEVICE_ROW_HEIGHT,
      minHeight: DEVICE_ROW_HEIGHT,
      maxHeight: DEVICE_ROW_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      padding: '0 8px 0 0',
      cursor: 'pointer',
      transition: SELECTION_TRANSITION,
      borderLeft: '3px solid transparent',
      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
      overflow: 'hidden',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      },
    },
    rowSelected: {
      borderLeftColor: '#00d4aa',
      backgroundColor: 'rgba(0, 212, 170, 0.08)',
      '&:hover': {
        backgroundColor: 'rgba(0, 212, 170, 0.10)',
      },
    },

    // Status dot
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      flexShrink: 0,
      marginRight: 6,
    },
    statusDotMoving: {
      animation: '$dotPulse 2s ease-in-out infinite',
    },
    '@keyframes dotPulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.5 },
    },

    // 2ECOMP: PremiumUI Refresh — coluna nome + timestamp
    nameColumn: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'hidden',
      justifyContent: 'center',
    },

    // 2ECOMP: PremiumUI Refresh — nome do device (fonte reduzida, igual GPSWOX)
    deviceName: {
      fontSize: '0.75rem',
      fontWeight: 400,
      color: isDark ? 'rgba(255,255,255,0.90)' : 'rgba(0,0,0,0.85)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: 1.3,
    },
    deviceNameOffline: {
      opacity: 0.5,
    },

    // 2ECOMP: PremiumUI Refresh — timestamp abaixo do nome
    timestamp: {
      fontSize: '0.65rem',
      color: isDark ? 'rgba(255,255,255,0.40)' : 'rgba(0,0,0,0.40)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: 1.2,
    },

    // 2ECOMP: PremiumUI Refresh — velocidade (fonte reduzida)
    speedBadge: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '0.68rem',
      fontWeight: 600,
      padding: '1px 5px',
      background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      borderRadius: '4px',
      flexShrink: 0,
      color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
      marginLeft: 4,
    },
    speedBadgeFast: {
      color: '#ef4444',
      background: 'rgba(239,68,68,0.15)',
    },

    // Camera badge
    cameraBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      marginLeft: 4,
      color: '#3b82f6',
      fontSize: '0.6rem',
      fontWeight: 700,
      flexShrink: 0,
    },

    // Botão ⋮
    moreButton: {
      padding: 2,
      flexShrink: 0,
      marginLeft: 2,
      color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)',
      '&:hover': {
        color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      },
      '& svg': { fontSize: 16 },
    },

    // Menu icons
    menuIcon: {
      minWidth: '28px !important',
      '& svg': { fontSize: 16 },
    },
    followMenuIcon: {
      color: '#10b981',
      minWidth: '28px !important',
      '& svg': { fontSize: 16 },
    },
    followMenuIconActive: {
      color: '#ef4444',
      minWidth: '28px !important',
      '& svg': { fontSize: 16 },
    },
  }
})

// ============================================
// COMPARADOR OTIMIZADO — 2ECOMP: PremiumUI Refresh (sem expand)
// ============================================
const arePropsEqual = (prevProps, nextProps) => {
  if (prevProps.device !== nextProps.device) return false
  if (prevProps.isSelected !== nextProps.isSelected) return false
  // 2ECOMP: grupos inline — comparar referência do array (memoizado no pai)
  if (prevProps.groups !== nextProps.groups) return false
  // 2ECOMP: PremiumUI Refresh — visibilidade (boolean, não array)
  if (prevProps.isHidden !== nextProps.isHidden) return false
  if (prevProps.onToggleVisibility !== nextProps.onToggleVisibility) return false

  const prevPos = prevProps.position
  const nextPos = nextProps.position
  if (!prevPos && !nextPos) return true
  if (!prevPos || !nextPos) return false
  return (
    prevPos.speed === nextPos.speed &&
    prevPos.fixTime === nextPos.fixTime &&
    prevPos.attributes?.motion === nextPos.attributes?.motion &&
    prevPos.attributes?.alarm === nextPos.attributes?.alarm
  )
}

// ============================================
// COMPONENTE PRINCIPAL — 2ECOMP: PremiumUI Refresh (48px)
// ============================================
// 2ECOMP: grupos inline — groups recebido como prop do DeviceList (evita re-render de 3000+ rows)
const DeviceRow = memo(({ device, position, isSelected, groups = [], isHidden = false, onToggleVisibility }) => {
  const { classes, cx } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const t = useTranslation()

  // Menu ⋮ state
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [commandModalOpen, setCommandModalOpen] = useState(false)
  // 2ECOMP: PremiumUI Refresh — "move to group" submenu
  const [moveAnchor, setMoveAnchor] = useState(null)
  const followPopupDeviceIds = useSelector((state) => state.devices.followPopupDeviceIds)
  const isFollowing = device ? followPopupDeviceIds.includes(device.id) : false

  if (!device) return null

  // Status calculation
  const effectiveStatus = getEffectiveStatus(
    device.status,
    position?.attributes?.motion,
    !!position
  )
  const hasAlarm = !!position?.attributes?.alarm
  const statusColor = getStatusColor(effectiveStatus, hasAlarm)
  const isOffline = effectiveStatus === 'offline' || effectiveStatus === 'unknown'
  const isMoving = effectiveStatus === 'moving'

  const speed = position?.speed ? Math.round(position.speed * 1.852) : 0
  const hasCamera = deviceHasCamera(device, position)

  // 2ECOMP: PremiumUI Refresh — click = selecionar (sem expand)
  const handleClick = useCallback(() => {
    dispatch(devicesActions.selectId(device.id))
  }, [dispatch, device.id])

  // Menu ⋮ handlers (preservados)
  const handleMenuOpen = useCallback((e) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
  }, [])

  const handleMenuClose = useCallback((e) => {
    if (e) e.stopPropagation()
    setMenuAnchor(null)
  }, [])

  const handleFollow = useCallback((e) => {
    e.stopPropagation()
    if (isFollowing) {
      dispatch(devicesActions.removeFollowPopup(device.id))
    } else {
      dispatch(devicesActions.addFollowPopup(device.id))
    }
    setMenuAnchor(null)
  }, [dispatch, device.id, isFollowing])

  const handleEvents = useCallback((e) => {
    e.stopPropagation()
    setMenuAnchor(null)
    const now = new Date()
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
    navigate(`/eventmap?deviceId=${device.id}&from=${encodeURIComponent(startOfDay.toISOString())}&to=${encodeURIComponent(now.toISOString())}`)
  }, [navigate, device.id])

  const handleHistory = useCallback((e) => {
    e.stopPropagation()
    setMenuAnchor(null)
    navigate(`/replay?deviceId=${device.id}`)
  }, [navigate, device.id])

  const handleCommands = useCallback((e) => {
    e.stopPropagation()
    setMenuAnchor(null)
    setCommandModalOpen(true)
  }, [])

  const handleEdit = useCallback((e) => {
    e.stopPropagation()
    setMenuAnchor(null)
    navigate(`/settings/device/${device.id}`)
  }, [navigate, device.id])

  // 2ECOMP: PremiumUI Refresh — "move to group" handlers
  const handleMoveOpen = useCallback((e) => {
    e.stopPropagation()
    setMoveAnchor(e.currentTarget)
  }, [])

  const handleMoveClose = useCallback(() => {
    setMoveAnchor(null)
  }, [])

  const handleMoveToGroup = useCallback(async (groupId) => {
    setMoveAnchor(null)
    setMenuAnchor(null)
    try {
      const response = await fetch(`/api/devices/${device.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...device, groupId: groupId || 0 }),
      })
      if (response.ok) {
        const updated = await response.json()
        dispatch(devicesActions.update([updated]))
      }
    } catch {
      // silently fail
    }
  }, [device, dispatch])

  return (
    <Box
      className={cx(classes.row, isSelected && classes.rowSelected)}
      onClick={handleClick}
    >
      {/* 2ECOMP: PremiumUI Refresh — checkbox visibilidade igual GPSWOX */}
      {onToggleVisibility && (
        <Checkbox
          size="small"
          checked={!isHidden}
          onChange={(e) => {
            e.stopPropagation()
            onToggleVisibility(device.id)
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            p: '2px',
            '& .MuiSvgIcon-root': { fontSize: 16 },
            color: 'text.disabled',
            '&.Mui-checked': { color: 'primary.main' },
            flexShrink: 0,
          }}
        />
      )}

      {/* Status dot */}
      <Box
        className={cx(classes.statusDot, isMoving && classes.statusDotMoving)}
        sx={{ backgroundColor: statusColor }}
      />

      {/* Alarm indicator */}
      {hasAlarm && (
        <Typography component="span" sx={{ fontSize: '0.7rem', mr: 0.5, flexShrink: 0 }}>
          🚨
        </Typography>
      )}

      {/* 2ECOMP: PremiumUI Refresh — nome + timestamp (2 linhas) */}
      <Box className={classes.nameColumn}>
        <Typography
          className={cx(classes.deviceName, isOffline && classes.deviceNameOffline)}
          component="span"
        >
          {device.name}
          {hasCamera && <span className={classes.cameraBadge}>CAM</span>}
        </Typography>
        {position?.fixTime && (
          <Typography className={classes.timestamp} component="span">
            {formatTime(position.fixTime, 'seconds')}
          </Typography>
        )}
      </Box>

      {/* Speed badge */}
      {speed > 0 && (
        <span className={cx(classes.speedBadge, speed > 80 && classes.speedBadgeFast)}>
          {speed}
        </span>
      )}

      {/* Menu ⋮ — PRESERVADO INTACTO */}
      <IconButton
        className={classes.moreButton}
        onClick={handleMenuOpen}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        <MenuItem onClick={handleFollow} dense>
          <ListItemIcon className={isFollowing ? classes.followMenuIconActive : classes.followMenuIcon}>
            <NavigationIcon />
          </ListItemIcon>
          <ListItemText>
            {isFollowing ? (t('deviceStopFollow') || 'Parar de seguir') : (t('deviceFollow') || 'Seguir')}
          </ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleEvents} dense>
          <ListItemIcon className={classes.menuIcon}>
            <EventNoteIcon />
          </ListItemIcon>
          <ListItemText>{t('reportEvents') || 'Eventos'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleHistory} dense>
          <ListItemIcon className={classes.menuIcon}>
            <HistoryIcon />
          </ListItemIcon>
          <ListItemText>{t('reportReplay') || 'Histórico'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCommands} dense>
          <ListItemIcon className={classes.menuIcon}>
            <TerminalIcon />
          </ListItemIcon>
          <ListItemText>{t('commandTitle') || 'Comando'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit} dense>
          <ListItemIcon className={classes.menuIcon}>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>{t('sharedEdit') || 'Editar'}</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        {/* 2ECOMP: PremiumUI Refresh — move to group */}
        <MenuItem onClick={handleMoveOpen} dense>
          <ListItemIcon className={classes.menuIcon}>
            <DriveFileMoveIcon />
          </ListItemIcon>
          <ListItemText>{t('groupMoveDevice') || 'Mover para grupo'}</ListItemText>
        </MenuItem>
      </Menu>
      {/* 2ECOMP: PremiumUI Refresh — submenu "move to group" */}
      <Menu
        anchorEl={moveAnchor}
        open={Boolean(moveAnchor)}
        onClose={handleMoveClose}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        <MenuItem
          onClick={() => handleMoveToGroup(0)}
          dense
          selected={!device.groupId}
        >
          <ListItemText>{t('groupNoGroup') || 'Sem grupo'}</ListItemText>
        </MenuItem>
        {groups.map((group) => (
            <MenuItem
              key={group.id}
              onClick={() => handleMoveToGroup(group.id)}
              dense
              selected={device.groupId === group.id}
            >
              <ListItemText>{group.name}</ListItemText>
            </MenuItem>
          ))}
      </Menu>

      {/* Command Modal */}
      <CommandModal
        open={commandModalOpen}
        onClose={() => setCommandModalOpen(false)}
        deviceId={device.id}
        deviceName={device.name}
        blocked={!!position?.attributes?.blocked}
        onSuccess={() => setCommandModalOpen(false)}
      />
    </Box>
  )
}, arePropsEqual)

DeviceRow.displayName = "DeviceRow"

export default DeviceRow
